import type { RuleId } from './constants';
export type { RuleId } from './constants';
export type { GameMode } from './constants';

export interface ToolDef {
  id: string;
  name: string;
  hint: string;
  color: number;
  /** logical size in world units */
  w: number;
  h: number;
  /** shape kind for drawing + physics */
  shape: 'rect' | 'tri' | 'circle' | 'capsule' | 'wedge';
  /** default unlocked */
  unlockDefault?: boolean;
  category: 'launcher' | 'mover' | 'surface' | 'special';
}

export const TOOLS: Record<string, ToolDef> = {
  spring: {
    id: 'spring',
    name: 'Spring',
    hint: 'Stores bounce energy',
    color: 0xff6b6b,
    w: 56,
    h: 28,
    shape: 'rect',
    unlockDefault: true,
    category: 'launcher',
  },
  fan: {
    id: 'fan',
    name: 'Fan',
    hint: 'Pushes objects with wind',
    color: 0x4ecdc4,
    w: 48,
    h: 48,
    shape: 'circle',
    unlockDefault: true,
    category: 'mover',
  },
  ramp: {
    id: 'ramp',
    name: 'Ramp',
    hint: 'Angle for launches',
    color: 0xffe66d,
    w: 90,
    h: 40,
    shape: 'wedge',
    unlockDefault: true,
    category: 'surface',
  },
  balloon: {
    id: 'balloon',
    name: 'Balloon',
    hint: 'Lifts upward gently',
    color: 0xff8fab,
    w: 40,
    h: 52,
    shape: 'capsule',
    category: 'mover',
  },
  magnet: {
    id: 'magnet',
    name: 'Magnet',
    hint: 'Pulls the bot closer',
    color: 0xc77dff,
    w: 44,
    h: 44,
    shape: 'circle',
    category: 'special',
  },
  crate: {
    id: 'crate',
    name: 'Crate',
    hint: 'Solid block mass',
    color: 0xc4a484,
    w: 48,
    h: 48,
    shape: 'rect',
    category: 'surface',
  },
  bounce_pad: {
    id: 'bounce_pad',
    name: 'Bounce Pad',
    hint: 'Super bouncy floor',
    color: 0x95e06c,
    w: 70,
    h: 22,
    shape: 'rect',
    category: 'launcher',
  },
  rocket: {
    id: 'rocket',
    name: 'Rocket',
    hint: 'Short thrust burst',
    color: 0xff9f1c,
    w: 36,
    h: 56,
    shape: 'capsule',
    category: 'launcher',
  },
  bumper: {
    id: 'bumper',
    name: 'Bumper',
    hint: 'Side kick on contact',
    color: 0x56cfe1,
    w: 40,
    h: 40,
    shape: 'circle',
    category: 'launcher',
  },
  portal_gate: {
    id: 'portal_gate',
    name: 'Boost Gate',
    hint: 'Speed ring boost',
    color: 0x9b5de5,
    w: 60,
    h: 60,
    shape: 'circle',
    category: 'special',
  },
};

export const DEFAULT_TOOL_ORDER = [
  'spring',
  'fan',
  'ramp',
  'balloon',
  'magnet',
  'crate',
  'bounce_pad',
  'rocket',
  'bumper',
  'portal_gate',
] as const;

export interface RuleDef {
  id: RuleId;
  name: string;
  short: string;
  gravityY: number;
  windX: number;
  worldRestitution: number;
  botRestitution: number;
  color: number;
}

export const RULES: Record<RuleId, RuleDef> = {
  normal: {
    id: 'normal',
    name: 'Normal Lab',
    short: 'Standard gravity',
    gravityY: 1.1,
    windX: 0,
    worldRestitution: 0.15,
    botRestitution: 0.35,
    color: 0x8ecae6,
  },
  low_gravity: {
    id: 'low_gravity',
    name: 'Low Gravity',
    short: 'Floaty hops',
    gravityY: 0.35,
    windX: 0,
    worldRestitution: 0.2,
    botRestitution: 0.45,
    color: 0xbdb2ff,
  },
  strong_wind: {
    id: 'strong_wind',
    name: 'Strong Wind',
    short: 'Constant push right',
    gravityY: 1.0,
    windX: 0.0016,
    worldRestitution: 0.15,
    botRestitution: 0.35,
    color: 0x90e0ef,
  },
  bouncy_world: {
    id: 'bouncy_world',
    name: 'Bouncy World',
    short: 'Everything bounces',
    gravityY: 1.05,
    windX: 0,
    worldRestitution: 0.85,
    botRestitution: 0.92,
    color: 0x80ed99,
  },
};

export interface ChallengeTemplate {
  id: string;
  title: string;
  goal: string;
  type: 'reach' | 'distance' | 'limited_parts' | 'rule_run' | 'survive';
  toolLimit?: number;
  allowedTools?: string[];
  rule: RuleId;
  targetDistance?: number;
  targetX?: number;
  surviveMs?: number;
  safeMinX?: number;
  safeMaxX?: number;
  starterTools?: { toolId: string; x: number; y: number; angle: number }[];
  unlockHint?: string;
}

export const CHALLENGES: ChallengeTemplate[] = [
  {
    id: 'ch_redline',
    title: 'Red Line Hop',
    goal: 'Launch the bot past the red line',
    type: 'reach',
    rule: 'normal',
    targetX: 520,
    allowedTools: ['spring', 'fan', 'ramp'],
    starterTools: [
      { toolId: 'ramp', x: 180, y: 0, angle: 0 },
      { toolId: 'spring', x: 140, y: 0, angle: 0 },
    ],
  },
  {
    id: 'ch_farther',
    title: 'Go Farther',
    goal: 'Reach at least 700 distance',
    type: 'distance',
    rule: 'normal',
    targetDistance: 700,
    allowedTools: ['spring', 'fan', 'ramp', 'bounce_pad', 'balloon'],
  },
  {
    id: 'ch_three_parts',
    title: 'Three Parts Only',
    goal: 'Pass the red line using at most 3 tools',
    type: 'limited_parts',
    rule: 'normal',
    toolLimit: 3,
    targetX: 520,
    allowedTools: ['spring', 'fan', 'ramp', 'bounce_pad', 'rocket'],
  },
  {
    id: 'ch_windy',
    title: 'Ride the Wind',
    goal: 'Under Strong Wind, reach the goal zone',
    type: 'rule_run',
    rule: 'strong_wind',
    targetX: 560,
    allowedTools: ['ramp', 'crate', 'fan', 'balloon', 'spring'],
  },
  {
    id: 'ch_hover',
    title: 'Stay Safe 5s',
    goal: 'Keep the bot in the safe zone for 5 seconds',
    type: 'survive',
    rule: 'low_gravity',
    surviveMs: 5000,
    safeMinX: 280,
    safeMaxX: 480,
    allowedTools: ['balloon', 'fan', 'magnet', 'crate', 'spring'],
  },
];

export function dailyChallenge(seed: number): ChallengeTemplate {
  const pick = CHALLENGES[seed % CHALLENGES.length];
  const rules: RuleId[] = ['normal', 'low_gravity', 'strong_wind', 'bouncy_world'];
  const rule = rules[seed % rules.length];
  return {
    ...pick,
    id: `daily_${pick.id}`,
    title: `Daily: ${pick.title}`,
    rule,
    goal: `${pick.goal} · today's rule: ${RULES[rule].name}`,
  };
}
